import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  X,
  MessageSquare,
} from "lucide-react";
import api from "../../utils/api";
import { toast } from "sonner";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination & Filtering
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const renderText = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    return text.en || text.ar || Object.values(text)[0] || "";
  };

  // Modals
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [currentLead, setCurrentLead] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    service_type: "",
    diagnosis: "",
    insurance_type: "",
    referral_source_id: "",
    notes: "",
    assigned_to: "",
    status: "new",
  });

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    fetchLeads();
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchAuxData();
  }, []);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "add") {
      handleOpenForm();
      // Clean up the URL to prevent re-opening on refresh
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location]);

  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get("/admin/leads", {
        params: { page, search, status: statusFilter },
      });
      setLeads(data.data);
      setTotalPages(data.meta?.last_page || 1);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuxData = async () => {
    try {
      const [sourcesRes, usersRes, servicesRes] = await Promise.all([
        api.get("/admin/referral-sources?per_page=100"),
        isAdmin
          ? api.get("/admin/users?per_page=100")
          : Promise.resolve({ data: { data: [currentUser] } }),
        api.get("/public/services?per_page=100"),
      ]);
      setSources(sourcesRes.data.data || []);
      setUsers(usersRes.data.data || []);
      setServices(servicesRes.data.data || []);
    } catch (error) {
      console.error("Failed to load aux data", error);
    }
  };

  const fetchNotes = async (leadId) => {
    try {
      const { data } = await api.get(`/admin/leads/${leadId}/notes`);
      setNotes(data.data);
    } catch (error) {
      toast.error("Failed to load notes");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenForm = (lead = null) => {
    if (lead) {
      setCurrentLead(lead);
      setFormData({
        name: lead.name || "",
        phone: lead.phone || "",
        email: lead.email || "",
        city: lead.city || "",
        service_type: lead.service_type || "",
        diagnosis: lead.diagnosis || "",
        insurance_type: lead.insurance_type || "",
        referral_source_id: lead.referral_source?.id || "",
        notes: lead.notes || "",
        assigned_to: lead.assigned_user?.id || "",
        status: lead.status || "new",
      });
    } else {
      setCurrentLead(null);
      setFormData({
        name: "",
        phone: "",
        email: "",
        city: "",
        service_type: "",
        diagnosis: "",
        insurance_type: "",
        referral_source_id: "",
        notes: "",
        assigned_to: "",
        status: "new",
      });
    }
    setIsFormOpen(true);
  };

  const handleOpenDetail = (lead) => {
    setCurrentLead(lead);
    fetchNotes(lead.id);
    setIsDetailOpen(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    try {
      // Clean up empty associations
      const payload = { ...formData };
      if (!payload.referral_source_id) delete payload.referral_source_id;
      if (!payload.assigned_to) delete payload.assigned_to;

      if (currentLead) {
        await api.put(`/admin/leads/${currentLead.id}`, payload);
        toast.success("Lead updated successfully");
      } else {
        await api.post("/admin/leads", payload);
        toast.success("Lead created successfully");
      }
      setIsFormOpen(false);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving lead");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error deleting lead");
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/leads/${currentLead.id}/notes`, {
        content: newNote,
      });
      setNewNote("");
      fetchNotes(currentLead.id);
      toast.success("Note added");
    } catch (err) {
      toast.error("Failed to add note");
    }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-800",
    contacted: "bg-purple-100 text-purple-800",
    qualified: "bg-indigo-100 text-indigo-800",
    pending: "bg-amber-100 text-amber-800",
    converted: "bg-emerald-100 text-emerald-800",
    lost: "bg-red-100 text-red-800",
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 relative">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage all incoming patient leads and inquiries
          </p>
        </div>

        <button
          onClick={() => handleOpenForm()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={20} />
          Add Lead
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <div className="w-full sm:w-48 relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="pending">Pending</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-500">
                <th className="px-6 py-4">Lead Info</th>
                <th className="px-6 py-4">Service</th>
                <th className="px-6 py-4">Assigned To</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No leads found.
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">
                        {lead.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {lead.phone || "No phone"} | {lead.city || "No city"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700 capitalize">
                        {renderText(lead.service_type)?.replace("_", " ") || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {lead.assigned_user ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                            {lead.assigned_user.name.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {lead.assigned_user.name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColors[lead.status] || "bg-gray-100 text-gray-800"}`}
                      >
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenDetail(lead)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenForm(lead)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(lead.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination placeholder if needed */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center bg-gray-50">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 bg-white border border-gray-200 rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 bg-white border border-gray-200 rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {currentLead ? "Edit Lead" : "Add New Lead"}
                </h2>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmitForm} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <input
                      required
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Service Type *
                    </label>
                    <select
                      required
                      name="service_type"
                      value={formData.service_type}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="" disabled>
                        Select a service
                      </option>
                      {services.map((s) => (
                        <option key={s.id} value={s.title?.en || s.title}>
                          {renderText(s.title)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Status *
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="pending">Pending</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>

                  {isAdmin && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700">
                        Assigned To
                      </label>
                      <select
                        name="assigned_to"
                        value={formData.assigned_to}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="">Unassigned</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Referral Source
                    </label>
                    <select
                      name="referral_source_id"
                      value={formData.referral_source_id}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Direct / Web Form</option>
                      {sources.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.facility_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-1 md:col-span-2 space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Diagnosis / Condition
                    </label>
                    <input
                      name="diagnosis"
                      value={formData.diagnosis}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2 space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Notes (Visible to staff)
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      value={formData.notes}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    ></textarea>
                  </div>
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
                  >
                    {currentLead ? "Save Changes" : "Create Lead"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {isDetailOpen && currentLead && (
          <div className="fixed inset-0 z-50 flex flex-col items-end sm:items-center justify-center sm:p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className="bg-gray-50 rounded-l-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col ml-auto sm:mx-auto"
            >
              <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shrink-0">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {currentLead.name}
                  </h2>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${statusColors[currentLead.status]?.split(" ")[0] || "bg-gray-400"}`}
                    ></span>
                    <span className="capitalize">{currentLead.status}</span> •{" "}
                    {currentLead.service_type?.replace("_", " ") || "-"}
                  </p>
                </div>
                <button
                  onClick={() => setIsDetailOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 flex flex-col md:flex-row gap-6">
                {/* Info sidebar */}
                <div className="w-full md:w-1/3 space-y-6">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-3 border-b pb-2">
                      Contact Info
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="text-gray-500">Phone:</span>{" "}
                        {currentLead.phone || "-"}
                      </p>
                      <p>
                        <span className="text-gray-500">Email:</span>{" "}
                        {currentLead.email || "-"}
                      </p>
                      <p>
                        <span className="text-gray-500">City:</span>{" "}
                        {currentLead.city || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-3 border-b pb-2">
                      Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="text-gray-500">Assigned:</span>{" "}
                        {currentLead.assigned_user?.name || "Unassigned"}
                      </p>
                      <p>
                        <span className="text-gray-500">Source:</span>{" "}
                        {currentLead.referral_source?.facility_name || "Direct"}
                      </p>
                      <p>
                        <span className="text-gray-500">Diagnosis:</span>{" "}
                        {currentLead.diagnosis || "-"}
                      </p>
                      <p>
                        <span className="text-gray-500">Insurance:</span>{" "}
                        {currentLead.insurance_type || "-"}
                      </p>
                    </div>
                  </div>

                  {currentLead.notes && (
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                      <h3 className="font-semibold text-blue-900 mb-2">
                        Initial Notes
                      </h3>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap">
                        {currentLead.notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes section */}
                <div className="w-full md:w-2/3 flex flex-col h-full min-h-[400px]">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare size={18} /> Activity & Notes
                  </h3>

                  <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
                    {notes.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 bg-white rounded-xl border border-gray-200 border-dashed">
                        No activity notes yet. Keep track of calls and updates
                        here.
                      </div>
                    ) : (
                      notes.map((note) => (
                        <div
                          key={note.id}
                          className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium text-sm text-gray-900">
                              {note.user?.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(note.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {note.content}
                          </p>
                        </div>
                      ))
                    )}
                  </div>

                  <form
                    onSubmit={handleAddNote}
                    className="mt-auto pt-4 border-t border-gray-200"
                  >
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add a new note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      <button
                        type="submit"
                        disabled={!newNote.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition"
                      >
                        Add Note
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
